import { createContext, useContext, type ReactNode } from 'react';
import { languages } from '#common/i18n/config';

export interface PathTranslationOptions {
    defaultLang: string;
    showDefaultLang?: boolean;
}

// /path -> /path & /de/path
// /#anchor -> /#anchor & /de#anchor
// /path#anchor -> /path#anchor / /de/path#anchor
export function translatePath(path: string, lang: string, options: PathTranslationOptions) {
    if (!path.startsWith('/')) {
        throw new Error('Only absolute paths are supported');
    }
    if (!options.showDefaultLang && lang === options.defaultLang) {
        return path;
    }
    if (path.startsWith('/#')) {
        path = path.substring(1);
    }
    return `/${lang}${path}`;
}

export function useTranslatedPath(options?: PathTranslationOptions): (path: string, l: string) => string;
export function useTranslatedPath(lang: string, options?: PathTranslationOptions): (path: string) => string;
export function useTranslatedPath(langOrOptions?: string | PathTranslationOptions, o?: PathTranslationOptions) {
    const defaultLang = useDefaultLang();
    let lang: string | undefined;
    let options: PathTranslationOptions | undefined;
    if (typeof langOrOptions === 'string') {
        lang = langOrOptions;
        options = o;
    } else {
        lang = undefined;
        options = langOrOptions;
    }
    options = options || { defaultLang };
    if (lang) {
        return (path: string, l: string = lang) => translatePath(path, l, options);
    } else {
        return (path: string, l: string) => translatePath(path, l, options);
    }
}

export function getUrlWithoutLang(url: string) {
    const parsedUrl = new URL(url);
    const [ , lang, ...rest ] = parsedUrl.pathname.split('/');
    if (lang && lang in languages) {
        return `/${rest.join('/')}`;
    }

    return parsedUrl.pathname;
}

export function getLanguageFromPath(path: string) {
    const pathParts = path.split('/');
    return (pathParts.length > 1 && pathParts[1]! in languages) ? pathParts[1] : undefined;
}

export function getLanguageFromUrl(url: string) {
    const parsedUrl = new URL(url);
    return getLanguageFromPath(parsedUrl.pathname);
}

const DefaultLangContext = createContext<string>('en');

export interface DefaultLangProviderProps {
    value: string;
    children: ReactNode;
}

export function DefaultLangProvider({ value, children }: DefaultLangProviderProps) {
    return <DefaultLangContext.Provider value={value}>{children}</DefaultLangContext.Provider>;
}

export function useDefaultLang(): string {
    return useContext(DefaultLangContext);
}
